import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import {
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { BipTemplateService } from '../../bip-template.service';
import { CommonService } from '../../../../shared/services/common.service';
import { LoaderService } from '../../../../core/services/loader.service';
import {
  DrawerParams,
  FilterFormValues,
  OrgTemplate,
  TemplateUserResponse,
} from '../../bip-template.interface';
import { LookupItem } from '../../../../core/models/lookup.models';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { DrawerService } from '../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { OrganizationDetailModel } from '../../../organizations/organization.interface';

@Component({
  selector: 'app-template-logs-filter',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzSelectModule,
    NzDatePickerModule,
    SvgIconComponent,
    NzButtonModule,
    ReactiveFormsModule,
    NzSpinModule,
    CommonModule,
  ],
  templateUrl: './template-logs-filter.component.html',
})
export class TemplateLogsFilterComponent implements OnDestroy {
  templateLogsFilterForm!: FormGroup;
  orgId = 0;
  userList: TemplateUserResponse[] = [];
  auditTypeList: LookupItem[] = [];
  templateList: OrgTemplate[] = [];
  orgData: OrganizationDetailModel | null = null;
  loading$: Observable<boolean> = of(false);
  private readonly destroy$ = new Subject<void>();
  constructor(
    private readonly fb: FormBuilder,
    private readonly templateService: BipTemplateService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly drawerService: DrawerService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.templateLogsFilterForm = this.fb.group({
      templateId: [null],
      dataAuditTypeId: [null],
      userId: [null],
      dateRange: [this.getDefaultDateRange()],
    });
    this.drawerService
      .getParams<DrawerParams>(DRAWER_IDS.TEMPLATE_LOGS_FILTER)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        this.orgId = params?.orgnizationId || 0;
        if (params?.appliedFilters) {
          this.templateLogsFilterForm.patchValue(params.appliedFilters);
        }
        this.getMasterData();
      });
  }

  async getMasterData() {
    this.loaderService.show();
    await Promise.all([
      this.templateService
        .getTemplateLogsLookup(Number(this.orgId))
        .toPromise(),
      this.commonService.getDataByType('DataAuditType').toPromise(),
      this.templateService
        .getBipTemplateByOrgId(Number(this.orgId))
        .toPromise(),
    ])
      .then(([lookupResponse, auditTypeList, templateList]) => {
        this.userList = lookupResponse?.result
          ? lookupResponse.result.templateUserReponses
          : [];
        this.auditTypeList = auditTypeList?.result ?? [];
        this.templateList = templateList?.result ?? [];
      })
      .catch(e => console.error(e))
      .finally(() => this.loaderService.hide());
  }

  reset() {
    this.templateLogsFilterForm.reset({
      templateId: null,
      dataAuditTypeId: null,
      userId: null,
      dateRange: this.getDefaultDateRange(),
    });
    const filterValues = this.templateLogsFilterForm.value;
    if (filterValues.dateRange?.length === 2) {
      filterValues.startDate = this.formatDateOnly(
        this.toStartOfDay(new Date(filterValues.dateRange[0]))
      );
      filterValues.endDate = this.formatDateOnly(
        this.toEndOfDay(new Date(filterValues.dateRange[1]))
      );
    } else {
      filterValues.startDate = null;
      filterValues.endDate = null;
    }
    const appliedFilterLength = this.calculateAppliedFilterLength(filterValues);
    this.drawerService.setPersistentState(DRAWER_IDS.TEMPLATE_LOGS_FILTER, {
      appliedFilters: filterValues,
      appliedFilterLength: appliedFilterLength,
    });
    const drawerRef = this.drawerService.getDrawerRef(
      DRAWER_IDS.TEMPLATE_LOGS_FILTER
    );
    drawerRef?.close({ filterValues });
  }

  apply() {
    const filterValues = this.templateLogsFilterForm.value;
    if (filterValues.dateRange?.length === 2) {
      filterValues.startDate = this.formatDateOnly(
        this.toStartOfDay(new Date(filterValues.dateRange[0]))
      );
      filterValues.endDate = this.formatDateOnly(
        this.toEndOfDay(new Date(filterValues.dateRange[1]))
      );
    } else {
      filterValues.startDate = null;
      filterValues.endDate = null;
    }
    const appliedFilterLength = this.calculateAppliedFilterLength(filterValues);
    this.drawerService.setPersistentState(DRAWER_IDS.TEMPLATE_LOGS_FILTER, {
      appliedFilters: filterValues,
      appliedFilterLength: appliedFilterLength,
    });
    const drawerRef = this.drawerService.getDrawerRef(
      DRAWER_IDS.TEMPLATE_LOGS_FILTER
    );
    drawerRef?.close({ filterValues });
  }

  private getDefaultDateRange(): [Date, Date] {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 6);
    start.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  }

  private formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toStartOfDay(date: Date): Date {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    return start;
  }

  private toEndOfDay(date: Date): Date {
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return end;
  }

  private calculateAppliedFilterLength(values: FilterFormValues): number {
    let count = 0;
    if (values.templateId) count += 1;
    if (values.dataAuditTypeId) count += 1;
    if (values.userId) count += 1;
    if (values.dateRange?.length === 2) count += 1;
    return count;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
